import { useEffect, useRef, useState } from "react";
import { Brain, SlidersHorizontal, Speaker, Loader2 } from "lucide-react";
import { API } from "@/assets";
import { useSiteAuth } from "@/components/GoogleAuth";
import { MedusaEngine } from "@/components/medusa/engine";
import { SectionShell } from "@/components/medusa/SectionShell";
import { BeatMemoryTrainer, FixTheMixTrainer, Mastery808Trainer } from "@/components/medusa/AcademyTrainers";

const MODULE_META = {
  "beat-memory": { Icon: Brain, color: "#00FF66" },
  "fix-the-mix": { Icon: SlidersHorizontal, color: "#00F0FF" },
  "808-mastery": { Icon: Speaker, color: "#FFB800" },
};

export default function AcademyPage() {
  const { profile } = useSiteAuth();
  const engine = useRef(null);
  if (!engine.current && typeof window !== "undefined") engine.current = new MedusaEngine();

  const [modules, setModules] = useState(null);
  const [active, setActive] = useState(null);
  const [memoryCleared, setMemoryCleared] = useState(0);

  useEffect(() => {
    fetch(`${API}/academy/content`).then((r) => r.json()).then((d) => setModules(d.modules || [])).catch(() => setModules([]));
  }, []);
  useEffect(() => {
    if (!profile) return;
    fetch(`${API}/players/progress`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { progress: [] }))
      .then((d) => {
        const mem = (d.progress || []).find((p) => p.ref_type === "drill" && p.ref_id === "beat-memory");
        if (mem) setMemoryCleared(Math.min(mem.best_score, 2));
      })
      .catch(() => {});
  }, [profile]);

  const renderTrainer = (m) => {
    if (m.kind === "memory") return <BeatMemoryTrainer module={m} engine={engine} cleared={memoryCleared} onClose={() => setActive(null)} />;
    if (m.kind === "mix") return <FixTheMixTrainer module={m} engine={engine} onClose={() => setActive(null)} />;
    return <Mastery808Trainer module={m} engine={engine} onClose={() => setActive(null)} />;
  };

  return (
    <SectionShell
      kicker="Section IV · Studio Lab"
      title="Production Academy"
      blurb="Skills that transfer to real production: rhythmic memory, surgical mixing against a reference, and the low-end science of the 808. Every cleared module pays Academy XP."
      color="#00F0FF"
      testId="academy-page"
    >
      {modules === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-teal-400" size={26} /></div>
      ) : active ? (
        renderTrainer(active)
      ) : (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="academy-modules">
          {modules.map((m) => {
            const meta = MODULE_META[m.module_id] || MODULE_META["beat-memory"];
            return (
              <button key={m.module_id} onClick={() => setActive(m)}
                className="text-left rounded-2xl border bg-[#0B0B10]/80 p-5 transition-all active:scale-[0.99] hover:shadow-[0_0_28px_rgba(0,240,255,0.12)]"
                style={{ borderColor: `${meta.color}44` }}
                data-testid={`module-card-${m.module_id}`}>
                <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}18`, color: meta.color }}>
                  <meta.Icon size={20} />
                </span>
                <p className="mt-3 font-display text-white font-bold">{m.title}</p>
                <p className="mt-1.5 text-xs text-ice/50 leading-relaxed line-clamp-4">{m.blurb}</p>
                <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.15em]" style={{ color: meta.color }}>
                  {m.kind === "memory" ? `3 levels · Level ${memoryCleared + 1} ready` : m.kind === "mix" ? "3 flawed sessions" : "3 drills"}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </SectionShell>
  );
}
